import type { MeasureMetric, MeasureRoom } from '@erp/shared';
import { api } from './client';
import type { OrganizationKind } from './organizations';

export interface EstimateLine {
  id: string;
  lineNumber: number;
  priceListItemId: string | null;
  sectionId: string | null;
  sectionName: string | null;
  name: string;
  code: string | null;
  unitId: string | null;
  unitCode: string | null;
  unitSymbol: string | null;
  measureMetric: MeasureMetric | null;
  measureRoomIds: string[];
  wastePercent: string | null;
  quantity: string;
  price: string;
  vatRateCode: string;
  vatRateValue: string | null;
  amount: string;
  vatAmount: string;
  totalAmount: string;
}

export interface EstimateLineInput {
  priceListItemId?: string | null;
  sectionId?: string | null;
  sectionName?: string | null;
  name: string;
  code?: string | null;
  unitId?: string | null;
  quantity: string;
  measureMetric?: MeasureMetric | null;
  measureRoomIds?: string[];
  wastePercent?: string | null;
  price: string;
  vatRateId: string;
}

export interface EstimateOrganizationRef {
  id: string;
  shortName: string;
  fullName: string;
  kind: OrganizationKind;
  inn: string;
  kpp: string | null;
  ogrn: string | null;
  addressFull: string | null;
  phone: string | null;
  email: string | null;
  directorPosition: string | null;
  directorName: string | null;
  logoDataUrl: string | null;
}

export interface EstimateCounterpartyRef {
  id: string;
  shortName: string;
  fullName: string;
  kind: OrganizationKind;
  inn: string | null;
  kpp: string | null;
  addressFull: string | null;
  actualAddressFull: string | null;
}

export interface Estimate {
  id: string;
  organizationId: string;
  organization: EstimateOrganizationRef;
  counterpartyId: string;
  counterparty: EstimateCounterpartyRef;
  number: string;
  numberInt: number;
  date: string;
  validUntil: string | null;
  objectName: string | null;
  objectAddress: string | null;
  measurements: MeasureRoom[] | null;
  /** Итоги уже со скидкой; сумма строк больше total на discountAmount. */
  subtotal: string;
  vatAmount: string;
  total: string;
  /** Скидка заказчику, %; null — без скидки. */
  discountPercent: string | null;
  discountAmount: string;
  comment: string | null;
  footerText: string | null;
  lines: EstimateLine[];
}

/** Поля шапки, общие для создания и редактирования. */
interface EstimateBody {
  date: string;
  validUntil?: string | null;
  objectName?: string | null;
  objectAddress?: string | null;
  comment?: string | null;
  footerText?: string | null;
  measurements?: MeasureRoom[] | null;
  discountPercent?: string | null;
  lines: EstimateLineInput[];
}

export interface EstimateCreateInput extends EstimateBody {
  organizationId: string;
  counterpartyId: string;
}

export type EstimateUpdateInput = EstimateBody;

export interface EstimateListItem {
  id: string;
  organizationId: string;
  organization: { id: string; shortName: string };
  counterpartyId: string;
  counterparty: { id: string; shortName: string };
  number: string;
  date: string;
  objectName: string | null;
  total: string;
}

export interface EstimateListResponse {
  items: EstimateListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface EstimateListParams {
  search?: string;
  organizationId?: string;
  counterpartyId?: string;
  page?: number;
  pageSize?: number;
}

function buildQuery(params: EstimateListParams): string {
  const search = new URLSearchParams();
  if (params.search) search.set('search', params.search);
  if (params.organizationId) search.set('organizationId', params.organizationId);
  if (params.counterpartyId) search.set('counterpartyId', params.counterpartyId);
  if (params.page !== undefined) search.set('page', String(params.page));
  if (params.pageSize !== undefined) search.set('pageSize', String(params.pageSize));
  const query = search.toString();
  return query ? `?${query}` : '';
}

export const estimatesApi = {
  list: (params: EstimateListParams = {}) => api.get<EstimateListResponse>(`/estimates${buildQuery(params)}`),

  get: (id: string) => api.get<{ estimate: Estimate }>(`/estimates/${id}`),

  create: (data: EstimateCreateInput) => api.post<{ estimate: Estimate }>('/estimates', data),

  update: (id: string, data: EstimateUpdateInput) => api.patch<{ estimate: Estimate }>(`/estimates/${id}`, data),

  remove: (id: string) => api.delete<void>(`/estimates/${id}`),
};
